import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, MapPin, Clock, ArrowRight, Sparkles, X } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import JobCard from "../components/JobCard";

const JOB_FORM_LINKS = {
  "Customer Service Representative": "https://forms.gle/iWLfQY85upkbNCSUA",
  "Technical Support Specialist": "https://forms.gle/iWLfQY85upkbNCSUA",
};

const jobDetails = {
  "Customer Service Representative": {
    responsibilities: [
      "Handle Tier 1 support via Zendesk (Email, Chat) with a focus on empathy and efficiency.",
      "Maintain a 90%+ Customer Satisfaction (CSAT) score.",
      "Document and escalate Tier 2 issues to the Technical Support team.",
      "Contribute to the internal knowledge base and FAQ documentation."
    ],
    requirements: [
      "1+ year experience in a remote customer support role.",
      "Excellent written English communication skills.",
      "Experience with Zendesk, Intercom, or similar support ticketing system.",
      "Ability to work independently and manage time effectively."
    ],
    package: [
      "Full-Time Contracted Employment (Remote).",
      "Competitive base salary (starting at 20,000 EGP) + Performance Bonuses.",
      "Comprehensive Health and Social Insurance."
    ]
  },
  "Technical Support Specialist": {
    responsibilities: [
      "Handle Tier 2 technical support, focusing on API integration, platform errors, and advanced troubleshooting.",
      "Write clear, detailed bug reports for the engineering team.",
      "Assist clients with integrating Pragma's solutions into their existing workflows.",
      "Maintain and update technical documentation and knowledge base articles."
    ],
    requirements: [
      "2+ years in a technical support role (SaaS/API experience preferred).",
      "Basic understanding of APIs (REST/JSON) and developer tools.",
      "Ability to read and understand basic code snippets (e.g., JavaScript, Python).",
      "Proven ability to manage high-stakes technical issues under pressure."
    ],
    package: [
      "Full-Time Contracted Employment (Remote).",
      "Competitive base salary (starting at 20,000 EGP) + Performance Bonuses.",
      "Comprehensive Health and Social Insurance."
    ]
  }
};

const jobs = [
  {
    id: 1,
    title: "Customer Service Representative",
    department: "Operations",
    location: "Remote",
    type: "Full-time",
    description: "We are looking for empathetic and tech-savvy individuals to provide world-class support for our growing list of clients.",
  },
  {
    id: 2,
    title: "Technical Support Specialist",
    department: "Engineering",
    location: "Remote",
    type: "Full-time",
    description: "Help our clients solve complex technical issues and integrate our solutions into their existing workflows.",
  }
];

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    window.scrollTo(0, 0);
  };

  const handleBackToList = () => {
    setSelectedJob(null);
    window.scrollTo(0, 0);
  };

  const JobDetailView = ({ job }) => {
    const details = jobDetails[job.title];
    const formLink = JOB_FORM_LINKS[job.title];

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <motion.button 
          onClick={handleBackToList}
          className="flex items-center gap-2 text-muted-foreground hover:text-cyan-400 transition-colors mb-8"
          whileHover={{ x: -4 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowRight className="w-4 h-4 rotate-180" /> Back to all openings
        </motion.button>

        <div className="p-8 border border-border bg-card/30 backdrop-blur-sm">
          <h1 className="text-foreground font-mono font-bold text-3xl mb-2">{job.title}</h1>
          <div className="flex items-center gap-4 text-muted-foreground/60 text-sm mb-8">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{job.type}</span>
          </div>

          <h3 className="text-foreground font-mono font-bold text-xl mb-4 border-b border-border pb-2">About the Role</h3>
          <p className="text-muted-foreground mb-8">{job.description}</p>

          <h3 className="text-foreground font-mono font-bold text-xl mb-4 border-b border-border pb-2">Key Responsibilities</h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-8">
            {details.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
          </ul>

          <h3 className="text-foreground font-mono font-bold text-xl mb-4 border-b border-border pb-2">Requirements & Qualifications</h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-8">
            {details.requirements.map((r, i) => <li key={i}>{r}</li>)}
          </ul>

          <h3 className="text-foreground font-mono font-bold text-xl mb-4 border-b border-border pb-2">Employment Package</h3>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-8">
            {details.package.map((p, i) => <li key={i}>{p}</li>)}
          </ul>

          <motion.a
            href={formLink}
            target="_blank"
            rel="noopener noreferrer"
            className="neon-button text-lg w-full text-center mt-6"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Apply Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </motion.a>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-32 pb-24">
        <div className="container">
          <AnimatePresence mode="wait">
            {selectedJob ? (
              <JobDetailView key="detail" job={selectedJob} />
            ) : (
              <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <motion.div 
                  className="text-center max-w-3xl mx-auto mb-20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <motion.span
                    className="inline-block px-4 py-1.5 bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-sm font-mono rounded-full mb-6"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Join Our Elite Team
                  </motion.span>
                  <h1 className="text-foreground mb-6">
                    Build the Future of <span className="text-cyan-400">Support</span>
                  </h1>
                  <p className="text-muted-foreground text-lg">
                    We're on a mission to redefine customer service. Join our global team of professionals and help us scale the world's most ambitious companies.
                  </p>
                </motion.div>

                <div className="grid gap-6 max-w-4xl mx-auto">
                  {jobs.map((job) => (
                    <JobCard key={job.id} job={job} onSelect={handleSelectJob} />
                  ))}
                </div>

                <motion.div 
                  className="mt-32 p-12 border border-border bg-card/20 relative overflow-hidden"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <Sparkles className="w-24 h-24 text-cyan-400" />
                  </div>
                  
                  <div className="relative z-10 grid md:grid-cols-3 gap-12">
                    <div>
                      <h4 className="text-foreground font-mono font-bold mb-4 uppercase tracking-wider">Remote First</h4>
                      <p className="text-muted-foreground text-sm">Work from anywhere in the world. We believe in talent over location.</p>
                    </div>
                    <div>
                      <h4 className="text-foreground font-mono font-bold mb-4 uppercase tracking-wider">Growth Mindset</h4>
                      <p className="text-muted-foreground text-sm">Continuous learning and development opportunities for every team member.</p>
                    </div>
                    <div>
                      <h4 className="text-foreground font-mono font-bold mb-4 uppercase tracking-wider">Elite Culture</h4>
                      <p className="text-muted-foreground text-sm">Join a team of high-performers dedicated to excellence in everything we do.</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <Footer />
    </div>
  );
}
